'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useChatStore } from '@/store/useChatStore';
import { useEffect, useState } from 'react';
import { X, Home, MessageSquare, HelpCircle, Trash2 } from 'lucide-react';
import { HomeTab } from './tabs/HomeTab';
import { MessagesTab } from './tabs/MessagesTab';
import { HelpTab } from './tabs/HelpTab';

type TabType = 'home' | 'messages' | 'help';

export function ChatWindow() {
  const {
    isOpen,
    toggleChat,
    messages,
    sendMessage,
    clearError,
    retrySendMessage,
    clearChat,
    addMessage,
  } = useChatStore();

  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('messages');
  const [hasShownProactiveGreeting, setHasShownProactiveGreeting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      sendMessage(input);
      setInput('');
      setActiveTab('messages');
    }
  };

  const handleRetry = () => {
    clearError();
    retrySendMessage();
  };

  // Proactive greeting after 10 seconds
  useEffect(() => {
    if (!hasShownProactiveGreeting && typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        if (!localStorage.getItem('chat_interacted')) {
          toggleChat();
          setActiveTab('messages');
          addMessage({
            content: "Hi! 👋 How can I help you today? I'm here to answer any questions about our childcare services.",
            role: 'assistant',
          });
          setHasShownProactiveGreeting(true);
        }
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [hasShownProactiveGreeting, addMessage, toggleChat]);

  // Mark as interacted when user opens chat
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      localStorage.setItem('chat_interacted', 'true');
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        toggleChat();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggleChat]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed z-[100] overflow-hidden bg-white shadow-2xl rounded-xl flex flex-col
            bottom-4 right-4 w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)]
            sm:bottom-24 sm:right-6 sm:w-96 sm:h-[540px] sm:max-h-[calc(100vh-7rem)]"
          style={{}}
        >
          {/* Header */}
          <div className="bg-[#ffc64a] text-[#35263a] p-4 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-[#08a997] rounded-full animate-pulse" />
              <h3 className="font-extrabold !text-[#35263a]">Cherie</h3>
            </div>
            <div className="flex items-center space-x-2">
              {messages.length > 0 && activeTab === 'messages' && (
                <button
                  onClick={clearChat}
                  className="p-1 rounded-full hover:bg-black/10 transition-colors"
                  title="Clear chat"
                >
                  <Trash2 size={18} />
                </button>
              )}
              <button
                onClick={toggleChat}
                className="p-1 rounded-full hover:bg-black/10 transition-colors"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden bg-gray-50">
            {/* {activeTab === 'home' && <HomeTab />} */}
            {activeTab === 'messages' && (
              <MessagesTab
                input={input}
                setInput={setInput}
                handleSubmit={handleSubmit}
                handleRetry={handleRetry}
              />
            )}
            {activeTab === 'help' && <HelpTab />}
          </div>

          {/* Tab Navigation */}
          <div className="border-t bg-white p-2 flex items-center justify-around">
            {/* <button
              onClick={() => setActiveTab('home')}
              className={`flex flex-col items-center justify-center py-2 px-4 rounded-lg transition-colors ${activeTab === 'home'
                ? 'text-[var(--color-primary)] bg-[#e0f5f2]'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <Home size={20} />
              <span className="text-xs mt-1 font-medium">Home</span>
            </button> */}
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex flex-col items-center justify-center py-2 px-4 rounded-lg transition-colors relative ${activeTab === 'messages'
                ? 'text-[var(--color-primary)] bg-[#e0f5f2]'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <MessageSquare size={20} />
              <span className="text-xs mt-1 font-medium">Chat</span>
              {messages.length > 0 && (
                <div className="absolute top-1 right-1 w-2 h-2 bg-[var(--color-primary)] rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('help')}
              className={`flex flex-col items-center justify-center py-2 px-4 rounded-lg transition-colors ${activeTab === 'help'
                ? 'text-[var(--color-primary)] bg-[#e0f5f2]'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <HelpCircle size={20} />
              <span className="text-xs mt-1 font-medium">FAQ</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}